import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';

const COLORS = ['#047857', '#059669', '#10b981', '#22c55e', '#84cc16', '#eab308', '#fde047', '#fefce8'];

export type PayrollConfettiOrigin = { x: number; y: number };

type Particle = {
  id: number;
  left: number;
  top: number;
  w: number;
  h: number;
  bx: string;
  by: string;
  x1: string;
  y1: string;
  x2: string;
  y2: string;
  x3: string;
  y3: string;
  x4: string;
  y4: string;
  x5: string;
  y5: string;
  rot: string;
  delay: string;
  dur: string;
  color: string;
};

function gravityY(by: number, fallTotal: number, u: number): number {
  const g = 1.72;
  return by + fallTotal * u ** g;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function PayrollConfettiBurstInner({
  origin,
  onComplete,
}: {
  origin: PayrollConfettiOrigin;
  onComplete: () => void;
}) {
  const particles = useMemo<Particle[]>(() => {
    const n = 52;
    const fallTotal = Math.max(420, (typeof window !== 'undefined' ? window.innerHeight : 800) - origin.y + 200);
    const burstEnd = 0.09;
    const fallSpan = 1 - burstEnd;
    const keyPct = [0.18, 0.32, 0.48, 0.64, 1.0] as const;
    const fallU = keyPct.map(p => (p - burstEnd) / fallSpan);

    return Array.from({ length: n }, (_, i) => {
      const burstR = 16 + Math.random() * 24;
      const burstA = Math.random() * Math.PI * 2;
      const bxPx = Math.cos(burstA) * burstR;
      const byPx = Math.sin(burstA) * burstR * 0.85 - 6;
      const bx = `${bxPx}px`;
      const by = `${byPx}px`;

      const drift = (Math.random() - 0.5) * 220 + Math.cos(burstA) * 48;
      const sway = (Math.random() - 0.5) * 90;
      const flutter = (Math.random() - 0.5) * 36;

      const xs = fallU.map(u => bxPx + drift * u + sway * Math.sin(u * Math.PI) + flutter * Math.sin(u * Math.PI * 2.4));
      const ys = fallU.map(u => gravityY(byPx, fallTotal, u));

      return {
        id: i,
        left: origin.x + (Math.random() - 0.5) * 6,
        top: origin.y + (Math.random() - 0.5) * 5,
        w: 3 + Math.random() * 7,
        h: 5 + Math.random() * 10,
        bx,
        by,
        x1: `${xs[0]!.toFixed(2)}px`,
        y1: `${ys[0]!.toFixed(2)}px`,
        x2: `${xs[1]!.toFixed(2)}px`,
        y2: `${ys[1]!.toFixed(2)}px`,
        x3: `${xs[2]!.toFixed(2)}px`,
        y3: `${ys[2]!.toFixed(2)}px`,
        x4: `${xs[3]!.toFixed(2)}px`,
        y4: `${ys[3]!.toFixed(2)}px`,
        x5: `${xs[4]!.toFixed(2)}px`,
        y5: `${ys[4]!.toFixed(2)}px`,
        rot: `${(Math.random() - 0.5) * 920}deg`,
        delay: `${Math.random() * 0.28}s`,
        dur: `${3.1 + Math.random() * 2.4}s`,
        color: COLORS[i % COLORS.length]!,
      };
    });
  }, [origin.x, origin.y]);

  useEffect(() => {
    const maxDurSec = 6.8;
    const t = window.setTimeout(onComplete, maxDurSec * 1000);
    return () => window.clearTimeout(t);
  }, [onComplete]);

  return createPortal(
    <div className="people-payroll-confetti-root" aria-hidden>
      {particles.map(p => (
        <div
          key={p.id}
          className="people-payroll-confetti-piece"
          style={
            {
              left: p.left,
              top: p.top,
              width: p.w,
              height: p.h,
              background: p.color,
              '--ppc-bx': p.bx,
              '--ppc-by': p.by,
              '--ppc-x1': p.x1,
              '--ppc-y1': p.y1,
              '--ppc-x2': p.x2,
              '--ppc-y2': p.y2,
              '--ppc-x3': p.x3,
              '--ppc-y3': p.y3,
              '--ppc-x4': p.x4,
              '--ppc-y4': p.y4,
              '--ppc-x5': p.x5,
              '--ppc-y5': p.y5,
              '--ppc-rot': p.rot,
              animationDelay: p.delay,
              animationDuration: p.dur,
            } as React.CSSProperties
          }
        />
      ))}
    </div>,
    document.body,
  );
}

let lastLaunchMs = 0;

/**
 * Full-viewport confetti falling from a viewport-fixed origin (e.g. center of the payroll $ marker).
 * Respects `prefers-reduced-motion`. Debounced to soften React StrictMode double-invoke.
 */
export function launchPayrollConfetti(origin: PayrollConfettiOrigin): void {
  if (typeof document === 'undefined') return;
  if (prefersReducedMotion()) return;

  const now = Date.now();
  if (now - lastLaunchMs < 400) return;
  lastLaunchMs = now;

  const mount = document.createElement('div');
  document.body.appendChild(mount);
  const root = createRoot(mount);
  const done = () => {
    try {
      root.unmount();
    } catch {
      /* ignore */
    }
    mount.remove();
  };

  root.render(
    <PayrollConfettiBurstInner
      origin={origin}
      onComplete={done}
    />,
  );
}
